import { driver } from "driver.js";
import "driver.js/dist/driver.css";

/**
 * Patient Dashboard Tour
 * 3-step walkthrough for first-time patient login
 * Shows: assigned games, therapy frequency, how to start
 * @param {Function} onComplete - Callback function to run after tour completes
 */
export const createPatientDashboardTour = (onComplete) => {
  const driverObj = driver({
    showProgress: true,
    showButtons: ['next', 'previous', 'close'],
    progressText: '{{current}} of 9', // Show total of 9 steps
    
    // Styling to match therapeutic app theme
    popoverClass: 'movemend-tour-popover',
    
    // Touch-friendly: Easy to dismiss but not by accident
    allowClose: true,
    overlayClickNext: false,
    
    // Smooth animations
    animate: true,
    
    // Disable keyboard navigation to prevent accidental dismissal on tablets
    disableActiveInteraction: false,
    
    // Steps for the dashboard tour
    steps: [
      {
        element: '.games-grid',
        popover: {
          title: 'Your Assigned Games',
          description: 'Here are all the games assigned to you by your therapist. Each game is designed to help with specific exercises.',
          side: 'bottom',
          align: 'center'
        }
      },
      {
        element: '.therapy-frequency',
        popover: {
          title: 'Exercise Schedule',
          description: 'Here\'s how often your therapist recommends doing your exercises. Following this schedule will help you progress faster.',
          side: 'top',
          align: 'center'
        }
      },
      {
        element: '.game-item:first-child',
        popover: {
          title: 'Ready to Play?',
          description: 'When you\'re ready, you can select any game to get started. The game will guide you through the exercise with real-time feedback.',
          side: 'right',
          align: 'start',
          doneBtnText: 'Next'
        }
      }
    ],
    
    // Callbacks
    onDestroyStarted: () => {
      // Mark tour as completed for this patient
      const patientId = localStorage.getItem('patientId');
      if (patientId) {
        localStorage.setItem(`dashboard_tour_completed_${patientId}`, 'true');
      }
      driverObj.destroy();
      
      // Call the completion callback if provided
      if (onComplete && typeof onComplete === 'function') {
        // Small delay to ensure tour is fully destroyed
        setTimeout(() => {
          onComplete();
        }, 100); // Reduced for smoother transition
      }
    }
  });

  return driverObj;
};

/**
 * Check if current patient should see the tour
 * Only shows once per patient (first-time login)
 */
export const shouldShowDashboardTour = () => {
  const patientId = localStorage.getItem('patientId');
  if (!patientId) return false;
  
  const hasCompletedTour = localStorage.getItem(`dashboard_tour_completed_${patientId}`);
  return !hasCompletedTour;
};

/**
 * Start the dashboard tour
 * Shows only for first-time patients
 * @param {Function} onComplete - Callback function to run after tour completes
 */
export const startDashboardTour = (onComplete) => {
  if (shouldShowDashboardTour()) {
    const tour = createPatientDashboardTour(onComplete);
    // Longer delay to ensure games are loaded and rendered
    setTimeout(() => {
      tour.drive();
    }, 1500);
  } else if (onComplete && typeof onComplete === 'function') {
    // If tour already seen, call callback immediately
    onComplete();
  }
};

/**
 * Force show the tour (for help button or testing)
 */
export const showDashboardTour = () => {
  const tour = createPatientDashboardTour();
  setTimeout(() => {
    tour.drive();
  }, 300);
};

/**
 * Reset tour for current patient (for testing)
 */
export const resetDashboardTour = () => {
  const patientId = localStorage.getItem('patientId');
  if (patientId) {
    localStorage.removeItem(`dashboard_tour_completed_${patientId}`);
  }
};
